// CSV no navegador — leitura da planilha do cliente SEM dependência nova.
//
// A importação de contatos é a primeira porta de entrada de quem chega com uma
// planilha na mão, então o parser aqui tem que aguentar o mundo real: aspas,
// vírgula dentro do campo, quebra de linha dentro do campo, CRLF do Windows,
// BOM do Excel e — o mais comum no Brasil — arquivo salvo com ponto-e-vírgula
// (o "CSV" que o Excel pt-BR gera por padrão).
//
// Nada aqui fala com a API: o mapeamento de colunas vira o corpo do
// POST /contacts/import, que é quem valida de verdade.

/**
 * decodeCsvBytes transforma os bytes do arquivo em texto.
 *
 * O Excel no Windows ainda salva "CSV" em Windows-1252: lido como UTF-8, o
 * arquivo vira "Jos<?>" e o nome do contato entra torto no banco. Então
 * tentamos UTF-8 e, se aparecer byte inválido (U+FFFD), relemos como 1252.
 */
export function decodeCsvBytes(buf: ArrayBuffer): string {
  const utf8 = new TextDecoder('utf-8').decode(buf);
  if (!utf8.includes('�')) return utf8;
  try {
    return new TextDecoder('windows-1252').decode(buf);
  } catch {
    return utf8; // navegador sem o rótulo: melhor o texto com furo do que nada
  }
}

/** Resultado da leitura: linhas, a linha do ARQUIVO de cada registro e o separador usado. */
export interface ParsedCsv {
  /** Registros (linhas em branco descartadas). */
  rows: string[][];
  /** lines[i] = linha (1-based) do arquivo onde o registro i começa. */
  lines: number[];
  /** Separador detectado/usado. */
  delimiter: string;
}

const DELIMITERS = [',', ';', '\t', '|'];

/** Remove o BOM que o Excel escreve no começo do arquivo. */
function stripBom(text: string): string {
  return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
}

/**
 * detectDelimiter olha as primeiras linhas FORA de aspas e escolhe o separador
 * que aparece de forma mais consistente. Vírgula empata a favor dela (padrão).
 */
export function detectDelimiter(text: string): string {
  const sample = stripBom(text).slice(0, 64_000);
  const counts: Record<string, number> = {};
  for (const d of DELIMITERS) counts[d] = 0;
  let inQuotes = false;
  let lines = 0;
  for (let i = 0; i < sample.length; i++) {
    const ch = sample[i];
    if (ch === '"') {
      if (inQuotes && sample[i + 1] === '"') { i++; continue; }
      inQuotes = !inQuotes;
      continue;
    }
    if (inQuotes) continue;
    if (ch === '\n') {
      lines++;
      if (lines >= 20) break;
      continue;
    }
    if (ch in counts) counts[ch]++;
  }
  let best = ',';
  for (const d of DELIMITERS) if (counts[d] > counts[best]) best = d;
  return counts[best] > 0 ? best : ',';
}

/**
 * parseCsv lê o texto inteiro em memória (o arquivo de contatos é pequeno: o
 * teto por chamada da API é 1000 linhas por lote).
 *
 * Regras (RFC 4180 + a realidade):
 * - campo entre aspas preserva separador e quebra de linha; `""` é uma aspa;
 * - aspas no meio de um campo não citado são literais ("3\" polegadas");
 * - \r\n, \n e \r terminam registro;
 * - registro totalmente vazio é descartado (mas a numeração das linhas do
 *   arquivo é preservada em `lines`, para o relatório de erro apontar a linha certa).
 */
export function parseCsv(text: string, delimiter?: string): ParsedCsv {
  const src = stripBom(text);
  const delim = delimiter ?? detectDelimiter(src);

  const rows: string[][] = [];
  const lines: number[] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let line = 1;          // linha atual do arquivo
  let rowLine = 1;       // linha onde o registro atual começou

  // flushRow fecha o registro atual e diz em que linha o PRÓXIMO começa.
  const flushRow = (nextLine: number) => {
    row.push(field);
    field = '';
    if (!row.every((c) => c.trim() === '')) { rows.push(row); lines.push(rowLine); }
    row = [];
    rowLine = nextLine;
  };

  for (let i = 0; i < src.length; i++) {
    const ch = src[i];

    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') { field += '"'; i++; continue; }
        inQuotes = false;
        continue;
      }
      if (ch === '\n') line++;
      field += ch;
      continue;
    }

    if (ch === '"' && field === '') { inQuotes = true; continue; }
    if (ch === '"') { field += '"'; continue; }
    if (ch === delim) { row.push(field); field = ''; continue; }
    if (ch === '\r') {
      if (src[i + 1] === '\n') i++;
      line++;
      flushRow(line);
      continue;
    }
    if (ch === '\n') {
      line++;
      flushRow(line);
      continue;
    }
    field += ch;
  }

  // Último registro sem quebra de linha no fim do arquivo.
  if (field !== '' || row.length > 0) flushRow(line);

  return { rows, lines, delimiter: delim };
}

/** Campos que a importação aceita (os de endereço entram em `address`). */
export type ImportField =
  | 'phone' | 'lid' | 'bsuid' | 'name' | 'email' | 'document' | 'document_type' | 'tags' | 'groups'
  | 'street' | 'number' | 'complement' | 'district' | 'city' | 'state' | 'zip' | 'country';

export const IMPORT_FIELDS: ImportField[] = [
  'phone', 'lid', 'bsuid', 'name', 'email', 'document', 'document_type', 'tags', 'groups',
  'street', 'number', 'complement', 'district', 'city', 'state', 'zip', 'country',
];

const ADDRESS_FIELDS = new Set<ImportField>([
  'street', 'number', 'complement', 'district', 'city', 'state', 'zip', 'country',
]);

/** normalizeHeader tira acento, caixa e pontuação: "Telefone (WhatsApp)" → "telefone whatsapp". */
export function normalizeHeader(h: string): string {
  return h
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

// Apelidos exatos por campo, nos idiomas que o painel fala + o jargão de planilha.
const ALIASES: Record<string, ImportField> = {};
const ALIAS_TABLE: [ImportField, string[]][] = [
  ['phone', ['telefone', 'telefone 1', 'celular', 'whatsapp', 'whats', 'fone', 'tel', 'phone', 'phone number', 'mobile', 'telefono', 'telefono movil', 'movil', 'msisdn', 'numero whatsapp', 'numero de whatsapp', 'telefon', 'telephone']],
  ['lid', ['lid', 'id whatsapp', 'id do whatsapp', 'whatsapp id', 'wa id', 'jid', 'id lid']],
  ['bsuid', ['bsuid', 'user id', 'meta user id', 'id meta', 'id da meta', 'id de usuario', 'business scoped user id']],
  ['name', ['nome', 'nome completo', 'name', 'full name', 'nombre', 'contato', 'contact', 'cliente', 'nom']],
  ['email', ['email', 'e mail', 'mail', 'correo', 'correo electronico', 'e post']],
  ['document', ['documento', 'cpf', 'cnpj', 'cpf cnpj', 'doc', 'document', 'nif', 'dni']],
  ['document_type', ['tipo de documento', 'tipo documento', 'document type', 'doc type', 'tipo doc']],
  ['tags', ['tag', 'tags', 'etiqueta', 'etiquetas', 'marcador', 'marcadores', 'labels']],
  ['groups', ['grupo', 'grupos', 'group', 'groups', 'lista', 'listas', 'segmento', 'gruppo', 'gruppi']],
  ['street', ['rua', 'logradouro', 'endereco', 'street', 'address', 'calle', 'direccion', 'via', 'strasse', 'adresse']],
  ['number', ['numero', 'num', 'n', 'number', 'nro', 'numero casa']],
  ['complement', ['complemento', 'complement', 'apto', 'apartamento', 'unidade']],
  ['district', ['bairro', 'district', 'barrio', 'neighborhood', 'quartier']],
  ['city', ['cidade', 'city', 'ciudad', 'municipio', 'localidade', 'citta', 'ville', 'stadt']],
  ['state', ['estado', 'uf', 'state', 'provincia', 'region', 'bundesland']],
  ['zip', ['cep', 'zip', 'zip code', 'postal', 'codigo postal', 'postcode', 'plz', 'cap']],
  ['country', ['pais', 'country', 'nazione', 'land']],
];
for (const [field, names] of ALIAS_TABLE) for (const n of names) ALIASES[n] = field;

// Palavras fortes: quando o cabeçalho não bate exatamente, ainda dá para acertar
// pelo pedaço ("Nº do WhatsApp do cliente" → phone).
const CONTAINS: [RegExp, ImportField][] = [
  [/\blid\b|\bjid\b/, 'lid'],
  [/bsuid/, 'bsuid'],
  [/whats|telefone|celular|phone|movil|msisdn/, 'phone'],
  [/mail|correo/, 'email'],
  [/cpf|cnpj|documento/, 'document'],
  [/tag|etiqueta|marcador/, 'tags'],
  [/grupo|group|segmento/, 'groups'],
  [/cidade|city|ciudad/, 'city'],
  [/bairro|barrio/, 'district'],
  [/cep|postal|zip/, 'zip'],
  [/nome|name|nombre/, 'name'],
];

/** guessField adivinha o campo de UM cabeçalho (null = não reconhecido). */
export function guessField(header: string): ImportField | null {
  const h = normalizeHeader(header);
  if (!h) return null;
  if (ALIASES[h]) return ALIASES[h];
  for (const [re, field] of CONTAINS) if (re.test(h)) return field;
  return null;
}

/**
 * guessMapping monta o de-para da planilha inteira. Um campo nunca é usado duas
 * vezes: a primeira coluna que casa fica com ele (a segunda vira "ignorar", e o
 * usuário corrige na tela se quiser).
 */
export function guessMapping(headers: string[]): (ImportField | null)[] {
  const used = new Set<ImportField>();
  return headers.map((h) => {
    const f = guessField(h);
    if (!f || used.has(f)) return null;
    used.add(f);
    return f;
  });
}

/**
 * looksLikeHeader: a primeira linha é cabeçalho quando reconhecemos algum nome
 * de coluna e ela NÃO parece um dado (telefone com dígitos, e-mail com @).
 */
export function looksLikeHeader(row: string[]): boolean {
  if (row.length === 0) return false;
  const recognized = row.filter((c) => guessField(c) !== null).length;
  if (recognized === 0) return false;
  const looksData = row.some((c) => /\d{6,}/.test(c) || c.includes('@'));
  return !looksData;
}

/** Só dígitos (o "+" é reposto na normalização). */
function digitsOf(v: string): string {
  return v.replace(/\D+/g, '');
}

const BSUID_RE = /^[A-Z]{2}\.[A-Za-z0-9.]{1,140}$/;

/**
 * normalizePhone devolve o formato do banco (`+DDIdigits`, sem espaços) ou ''
 * quando não há dígito nenhum. NÃO inventa DDI: número sem país é recusado pela
 * API com `invalid_phone`, e é isso que o ensaio tem que mostrar.
 */
export function normalizePhone(raw: string): string {
  const v = (raw ?? '').trim();
  // LID do WhatsApp ("123…@lid"): vai como está — a API entende e grava no lugar
  // certo. Dígitos de LID nunca viram telefone.
  if (/@lid$/i.test(v)) return digitsOf(v) + '@lid';
  // BSUID da Meta ("BR.1349…"): letras e ponto o distinguem de telefone. Também
  // vai como está; só o trilho oficial envia para ele.
  if (BSUID_RE.test(v)) return v;
  const d = digitsOf(v);
  return d ? '+' + d : '';
}

/**
 * guessByContent: sem cabeçalho, a coluna que traz telefones ou LIDs na maioria
 * das linhas vira o telefone — é o que deixa importar uma lista de uma coluna só.
 */
export function guessByContent(rows: string[][]): (ImportField | null)[] {
  const width = Math.max(0, ...rows.map((r) => r.length));
  const sample = rows.slice(0, 50);
  let best = -1, bestHits = 0;
  for (let col = 0; col < width; col++) {
    const hits = sample.filter((r) => (/^\+?[\d\s().-]{8,}$|@lid$/i.test((r[col] ?? '').trim()) || BSUID_RE.test((r[col] ?? '').trim()))).length;
    if (hits > bestHits) { best = col; bestHits = hits; }
  }
  return Array.from({ length: width }, (_, col) => (col === best && bestHits >= Math.ceil(sample.length / 2) ? 'phone' : null));
}

/** Célula com vários valores: "vip; churn" ou "vip, churn" → ['vip','churn']. */
export function splitList(value: string): string[] {
  return (value ?? '')
    .split(/[;,|]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export interface ImportAddress {
  street?: string; number?: string; complement?: string; district?: string;
  city?: string; state?: string; zip?: string; country?: string;
}

/** Linha pronta para o corpo do POST /contacts/import. */
export interface ImportContactRow {
  phone: string;
  lid?: string;
  bsuid?: string;
  name?: string;
  email?: string;
  document?: string;
  document_type?: string;
  address?: ImportAddress;
  tags?: string[];
  groups?: string[];
}

/** Linha da planilha já mapeada + de onde ela veio (para o relatório). */
export interface MappedRow {
  contact: ImportContactRow;
  /** Linha do arquivo (1-based) — é o que o usuário vê no relatório. */
  line: number;
}

/**
 * buildImportRows aplica o de-para e monta o corpo da importação. Campo vazio é
 * OMITIDO (a API nunca apaga o que já existe com valor em branco), e-mail vai em
 * minúsculas, telefone em `+DDIdigits`.
 */
export function buildImportRows(
  rows: string[][],
  mapping: (ImportField | null)[],
  lines?: number[],
): MappedRow[] {
  const out: MappedRow[] = [];
  rows.forEach((cells, i) => {
    const contact: ImportContactRow = { phone: '' };
    const address: ImportAddress = {};
    mapping.forEach((field, col) => {
      if (!field) return;
      const raw = (cells[col] ?? '').trim();
      if (!raw) return;
      if (ADDRESS_FIELDS.has(field)) { address[field as keyof ImportAddress] = raw; return; }
      switch (field) {
        case 'phone': contact.phone = normalizePhone(raw); break;
        case 'lid': contact.lid = /@lid$/i.test(raw) ? digitsOf(raw) + '@lid' : digitsOf(raw); break;
        case 'bsuid': contact.bsuid = raw; break;
        case 'email': contact.email = raw.toLowerCase(); break;
        case 'tags': contact.tags = splitList(raw); break;
        case 'groups': contact.groups = splitList(raw); break;
        case 'name': contact.name = raw; break;
        case 'document': contact.document = raw; break;
        case 'document_type': contact.document_type = raw.toLowerCase(); break;
      }
    });
    if (Object.keys(address).length > 0) contact.address = address;
    out.push({ contact, line: lines?.[i] ?? i + 1 });
  });
  return out;
}

/** Fatia em lotes do tamanho aceito pela API (teto de 1000 por chamada). */
export function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}
